/**
 * Property Type Slideout - Create and edit property types
 *
 * Provides a form for creating or editing custom property types with
 * appropriate field states, tooltips for disabled fields, and auto-generated names.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, RefreshCw01 } from '@untitledui/icons';
import {
  type ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { Button } from '@/lib/base/buttons/button';
import { Checkbox } from '@/lib/base/forms/checkbox';
import { FormProvider, useFormContext } from '@/lib/base/forms/form-context';
import { FormField } from '@/lib/base/forms/form-field';
import type { FieldConfig } from '@/lib/base/forms/hooks/useFormErrors';
import { Input } from '@/lib/base/forms/input/input';
import { Label } from '@/lib/base/forms/input/label';
import { Select } from '@/lib/base/forms/select/select';
import { SubmitButton } from '@/lib/base/forms/submit-button';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';
import { PropertyInput } from '@/lib/crm/components/properties/PropertyInput';
import type { SelectOption } from '@/lib/crm/components/properties/types';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import {
  getUserFriendlyEventError,
  PERMISSION_DENIED_MESSAGE,
} from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import {
  createSchemaClient,
  type FieldType,
  type PropertyType,
} from '@/lib/crm/core/SchemaClient';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import {
  generatePropertyName,
  getFieldTypeItems,
  getFieldTypeLabel,
  resolveNameCollision,
} from '@/lib/crm/utils/fieldTypeHelpers';
import { useViewHealthScope } from '@/lib/view-health';

import { SelectOptionsEditor } from './SelectOptionsEditor';

export interface PropertyTypeSlideoutProps {
  /** Whether the slideout is open */
  isOpen: boolean;
  /** Callback when the slideout is closed */
  onClose: () => void;
  /** Property to edit (undefined = create mode) */
  property?: PropertyType;
  /** The entity type name (contacts, companies, deals) */
  entityTypeName: string;
  /** Callback when the operation succeeds */
  onSuccess?: () => void;
}

interface FormState {
  label: string;
  name: string;
  fieldType: string;
  description: string;
  isUnique: boolean;
  defaultValue: string;
  options: SelectOption[];
}

const FIELD_LABELS = {
  label: 'Label',
  name: 'Name',
  fieldType: 'Field Type',
  options: 'Options',
} as const;

const noop = () => {};

// =============================================================================
// PropertyTypeFormContent — inner component that accesses FormContext
//
// Separated from the outer component so it can call useFormContext() as a
// descendant of FormProvider. Renders the slideout body and footer.
// =============================================================================

interface PropertyTypeFormContentProps {
  close: () => void;
  title: string;
  isEditMode: boolean;
  formState: FormState;
  updateField: <K extends keyof FormState>(
    field: K,
    value: FormState[K]
  ) => void;
  optionErrors: Record<number, { value?: string; label?: string }>;
  apiError: string | null;
  isSubmitting: boolean;
  validateAndApplyOptionErrors: () => Record<
    number,
    { value?: string; label?: string }
  >;
  onSave: () => Promise<void>;
  onClearApiError: () => void;
  fieldTypes: FieldType[];
  fieldTypesLoading: boolean;
  fieldTypesError: Error | null;
  crmTypeName: string;
  onCheckOptionUsage?: (optionValue: string) => Promise<number>;
}

function PropertyTypeFormContent({
  close,
  title,
  isEditMode,
  formState,
  updateField,
  optionErrors,
  apiError,
  isSubmitting,
  validateAndApplyOptionErrors,
  onSave,
  onClearApiError,
  fieldTypes,
  fieldTypesLoading,
  fieldTypesError,
  crmTypeName,
  onCheckOptionUsage,
}: PropertyTypeFormContentProps) {
  const { visibleErrors } = useFormContext();

  // SubmitButton handles touchAll/isValid/scrollToField automatically.
  // This handler only runs when FormProvider says the form is valid,
  // so we just need to check the custom option validation.
  const handleSubmitClick = useCallback(() => {
    const newOptionErrors = validateAndApplyOptionErrors();
    if (Object.keys(newOptionErrors).length > 0) {
      return;
    }
    void onSave();
  }, [validateAndApplyOptionErrors, onSave]);

  const handleRetry = useCallback(() => {
    onClearApiError();
    handleSubmitClick();
  }, [onClearApiError, handleSubmitClick]);

  const handleLabelChange = useCallback(
    (value: string) => updateField('label', value),
    [updateField]
  );

  const handleDescriptionChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) =>
      updateField('description', e.target.value),
    [updateField]
  );

  const handleFieldTypeChange = useCallback(
    (key: string | number | null) => updateField('fieldType', key as string),
    [updateField]
  );

  const handleOptionsChange = useCallback(
    (options: SelectOption[]) => updateField('options', options),
    [updateField]
  );

  const handleDefaultValueChange = useCallback(
    (value: unknown) =>
      updateField('defaultValue', value != null ? String(value) : ''),
    [updateField]
  );

  const handleIsUniqueChange = useCallback(
    (selected: boolean) => updateField('isUnique', selected),
    [updateField]
  );

  return (
    <>
      <SlideoutMenu.Header onClose={close}>
        <h2 className='text-lg font-semibold text-primary'>{title}</h2>
        <p className='text-sm text-tertiary mt-1'>
          {isEditMode
            ? 'Update the property configuration'
            : 'Create a new custom property'}
        </p>
      </SlideoutMenu.Header>

      <SlideoutMenu.Content>
        <div className='space-y-6 py-4'>
          {/* API Error Banner */}
          {apiError && (
            <div className='rounded-lg border border-utility-error-200 bg-utility-error-50 p-4'>
              <div className='flex items-start gap-3'>
                <FeaturedIcon
                  icon={AlertCircle}
                  size='sm'
                  theme='light-outline'
                  color='error'
                />
                <div className='flex-1'>
                  <h4 className='text-sm font-medium text-utility-error-700'>
                    {apiError === PERMISSION_DENIED_MESSAGE
                      ? 'Permission denied'
                      : 'Error'}
                  </h4>
                  <p className='text-sm text-utility-error-600 mt-1'>
                    {apiError}
                  </p>
                </div>
                {apiError !== PERMISSION_DENIED_MESSAGE && (
                  <Button
                    size='sm'
                    color='secondary'
                    iconLeading={RefreshCw01}
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Label Field */}
          <FormField name='label' label='Label' isRequired>
            {({ isInvalid, showError, inputId }) => (
              <Input
                id={inputId}
                placeholder='Enter property label'
                value={formState.label}
                onChange={handleLabelChange}
                isRequired
                isInvalid={isInvalid}
                showError={showError}
              />
            )}
          </FormField>

          {/* Description Field */}
          <div className='space-y-1.5'>
            <Label>Description</Label>
            <textarea
              placeholder='Enter property description (optional)'
              value={formState.description}
              onChange={handleDescriptionChange}
              rows={3}
              className='w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-primary placeholder:text-tertiary focus:border-brand-solid focus:outline-none focus:ring-1 focus:ring-brand-solid resize-none'
            />
          </div>

          {/* Field Type Field */}
          <FormField name='fieldType'>
            {({ isInvalid, showError, inputId }) =>
              isEditMode ? (
                <Tooltip
                  title='Field type cannot be changed after creation'
                  placement='top'
                >
                  <TooltipTrigger className='w-full text-left'>
                    <Input
                      id={inputId}
                      label='Field Type'
                      value={
                        fieldTypesLoading
                          ? 'Loading…'
                          : getFieldTypeLabel(formState.fieldType, fieldTypes)
                      }
                      isDisabled
                    />
                  </TooltipTrigger>
                </Tooltip>
              ) : (
                <>
                  <Select
                    id={inputId}
                    label='Field Type'
                    placeholder={
                      fieldTypesLoading ? 'Loading…' : 'Select field type'
                    }
                    items={getFieldTypeItems(fieldTypes)}
                    selectedKey={formState.fieldType}
                    onSelectionChange={handleFieldTypeChange}
                    isRequired
                    isDisabled={fieldTypesLoading}
                    isInvalid={isInvalid}
                    showError={showError}
                  >
                    {item => (
                      <Select.Item id={item.id}>{item.label}</Select.Item>
                    )}
                  </Select>
                  {fieldTypesError && (
                    <p className='text-sm text-error mt-1'>
                      We couldn't load field types
                    </p>
                  )}
                </>
              )
            }
          </FormField>

          {/* Options Editor - only for select type */}
          {formState.fieldType === 'select' && (
            <div data-form-field='options'>
              <SelectOptionsEditor
                options={formState.options}
                onChange={handleOptionsChange}
                disabled={isSubmitting}
                errors={optionErrors}
                onCheckOptionUsage={isEditMode ? onCheckOptionUsage : undefined}
              />
              {visibleErrors.options && (
                <p className='text-sm text-error-600 mt-1'>
                  {visibleErrors.options}
                </p>
              )}
            </div>
          )}

          {/* Default Value Field - hide for select with no options */}
          {(formState.fieldType !== 'select' ||
            formState.options.length > 0) && (
            <div className='space-y-1.5'>
              <Label>Default Value</Label>
              <PropertyInput
                propertyType={
                  {
                    property_type_name: formState.name || 'default_value',
                    entity_type_name: crmTypeName,
                    label: 'Default Value',
                    field_type_name: formState.fieldType,
                    description: '',
                    is_required: false,
                    is_unique_value: false,
                    value_write_access: 'all',
                    value_read_access: 'all',
                    value_update_access: 'all',
                    options: formState.options.map(opt => ({
                      value: opt.value,
                      label: opt.label,
                    })),
                  } as PropertyTypeSchema
                }
                value={formState.defaultValue}
                onChange={handleDefaultValueChange}
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Checkbox Fields */}
          <div className='space-y-4'>
            {isEditMode ? (
              <Tooltip
                title="This can't be changed after the property is created"
                placement='top'
              >
                <TooltipTrigger className='w-full text-left'>
                  <Checkbox
                    label='Require unique values'
                    hint='Each record must have a different value for this property.'
                    isSelected={formState.isUnique}
                    isDisabled
                  />
                </TooltipTrigger>
              </Tooltip>
            ) : (
              <Checkbox
                label='Require unique values'
                hint='Each record must have a different value for this property.'
                isSelected={formState.isUnique}
                onChange={handleIsUniqueChange}
              />
            )}
          </div>
        </div>
      </SlideoutMenu.Content>

      <SlideoutMenu.Footer>
        <div className='flex justify-end gap-3'>
          <Button size='sm' color='secondary' onClick={close}>
            Cancel
          </Button>
          <SubmitButton
            size='sm'
            color='primary'
            onClick={handleSubmitClick}
            isDisabled={isSubmitting}
          >
            {(() => {
              let buttonText: string;
              if (isSubmitting) {
                buttonText = isEditMode ? 'Saving…' : 'Creating…';
              } else {
                buttonText = isEditMode ? 'Save changes' : 'Create property';
              }
              return buttonText;
            })()}
          </SubmitButton>
        </div>
      </SlideoutMenu.Footer>
    </>
  );
}

// =============================================================================
// PropertyTypeSlideout - Main component
// =============================================================================

/**
 * Property Type Slideout component.
 */
export function PropertyTypeSlideout({
  isOpen,
  onClose,
  property,
  entityTypeName,
  onSuccess,
}: PropertyTypeSlideoutProps) {
  useViewHealthScope('overlay', 'property-type-slideout', isOpen);
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const orgId = currentOrg?.id;
  const userId = user?.id;
  const isEditMode = !!property;

  // Map tab ID to CRM type name
  const crmTypeName = entityTypeName;

  // Fetch field types from API
  const {
    data: fieldTypes = [],
    isLoading: fieldTypesLoading,
    error: fieldTypesError,
  } = useFieldTypes();

  // Form state
  const [formState, setFormState] = useState<FormState>({
    label: '',
    name: '',
    fieldType: 'text',
    description: '',
    isUnique: false,
    defaultValue: '',
    options: [],
  });

  // Baseline snapshot captured on open, used to compute hasUnsavedChanges.
  const initialFormStateRef = useRef<FormState>(formState);

  const [optionErrors, setOptionErrors] = useState<
    Record<number, { value?: string; label?: string }>
  >({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch existing property types for name collision detection
  const { data: propertyTypes } = useQuery({
    queryKey: ['propertyTypes', orgId, crmTypeName],
    queryFn: async () => {
      if (!orgId) {
        throw new Error('No organization selected');
      }
      const client = createSchemaClient(orgId);
      return client.getPropertyTypes(crmTypeName);
    },
    enabled: !!orgId && isOpen,
  });

  // Build set of existing property names
  const existingNames = useMemo(() => {
    if (!propertyTypes) {
      return new Set<string>();
    }
    return new Set(propertyTypes.map(p => p.property_type_name));
  }, [propertyTypes]);

  // Initialize form when property changes or slideout opens
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (property) {
      // Edit mode - populate form with existing values
      const initialState: FormState = {
        label: property.label,
        name: property.property_type_name,
        fieldType: property.field_type_name,
        description: property.description || '',
        isUnique: property.is_unique_value,
        defaultValue: '', // TODO: Add to PropertyType interface if backend supports it
        options: property.options || [],
      };
      setFormState(initialState);
      initialFormStateRef.current = initialState;
    } else {
      // Create mode - reset form
      const initialState: FormState = {
        label: '',
        name: '',
        fieldType: 'text',
        description: '',
        isUnique: false,
        defaultValue: '',
        options: [],
      };
      setFormState(initialState);
      initialFormStateRef.current = initialState;
    }

    setApiError(null);
  }, [isOpen, property]);

  // Validate default field type when field types load
  useEffect(() => {
    if (
      !isEditMode &&
      fieldTypes.length > 0 &&
      formState.fieldType === 'text'
    ) {
      const availableTypes = fieldTypes.filter(
        ft => ft.type_use_access !== 'internal'
      );
      const hasText = availableTypes.some(ft => ft.field_type_name === 'text');
      if (!hasText && availableTypes.length > 0) {
        const correctedFieldType = availableTypes[0].field_type_name;
        // Also correct the baseline so this auto-correction (which happens
        // shortly after open, before the user has touched anything) doesn't
        // register as a user-made change and cause a false-positive dirty.
        if (initialFormStateRef.current.fieldType === 'text') {
          initialFormStateRef.current = {
            ...initialFormStateRef.current,
            fieldType: correctedFieldType,
          };
        }
        setFormState(prev => ({
          ...prev,
          fieldType: correctedFieldType,
        }));
      }
    }
  }, [fieldTypes, isEditMode, formState.fieldType]);

  // Auto-generate name when label changes (create mode only)
  useEffect(() => {
    if (isEditMode) {
      return;
    }

    const baseName = generatePropertyName(formState.label);
    if (!baseName) {
      setFormState(prev => ({ ...prev, name: '' }));
      return;
    }

    const resolvedName = resolveNameCollision(baseName, existingNames);
    setFormState(prev => ({ ...prev, name: resolvedName }));
  }, [formState.label, existingNames, isEditMode]);

  // Update form field
  const updateField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K]) => {
      setFormState(prev => ({ ...prev, [field]: value }));
      // Clear option errors when options change (functional updater avoids stale closure)
      if (field === 'options') {
        setOptionErrors(prev => (Object.keys(prev).length > 0 ? {} : prev));
      }
    },
    []
  );

  // Validate select options and apply errors to state — returns the error map.
  const validateAndApplyOptionErrors = useCallback((): Record<
    number,
    { value?: string; label?: string }
  > => {
    if (formState.fieldType !== 'select') {
      return {};
    }

    const newOptionErrors: Record<number, { value?: string; label?: string }> =
      {};
    const seenValues = new Map<string, number>();
    const seenLabels = new Map<string, number>();

    formState.options.forEach((option, index) => {
      const value = option.value.trim();
      const label = option.label.trim().toLowerCase();

      // Check duplicate values
      if (value && seenValues.has(value)) {
        newOptionErrors[index] = {
          ...newOptionErrors[index],
          value: 'Duplicate value',
        };
        const firstIndex = seenValues.get(value);
        if (firstIndex !== undefined) {
          newOptionErrors[firstIndex] = {
            ...newOptionErrors[firstIndex],
            value: 'Duplicate value',
          };
        }
      } else if (value) {
        seenValues.set(value, index);
      }

      // Check duplicate labels
      if (label && seenLabels.has(label)) {
        newOptionErrors[index] = {
          ...newOptionErrors[index],
          label: 'Duplicate label',
        };
        const firstIndex = seenLabels.get(label);
        if (firstIndex !== undefined) {
          newOptionErrors[firstIndex] = {
            ...newOptionErrors[firstIndex],
            label: 'Duplicate label',
          };
        }
      } else if (label) {
        seenLabels.set(label, index);
      }
    });

    setOptionErrors(newOptionErrors);
    return newOptionErrors;
  }, [formState.fieldType, formState.options]);

  // FormProvider field validation config
  const fieldConfig = useMemo(() => {
    const config: {
      label: FieldConfig;
      name: FieldConfig;
      fieldType: FieldConfig;
      options?: FieldConfig;
    } = {
      label: {
        validate: (v: unknown) =>
          !String(v ?? '').trim() ? 'Label is required' : undefined,
      },
      name: {
        validate: (v: unknown) =>
          !String(v ?? '').trim() ? 'Name is required' : undefined,
      },
      fieldType: {
        validate: (v: unknown) => (!v ? 'Field type is required' : undefined),
      },
    };
    // Add top-level options validation for select type
    if (formState.fieldType === 'select') {
      config.options = {
        validate: (v: unknown) => {
          const opts = v as SelectOption[];
          if (!opts || opts.length < 2) {
            return 'Add 2 or more options to save changes.';
          }
          return undefined;
        },
      };
    }
    return config;
  }, [formState.fieldType]);

  // FormProvider current values
  const formValues = useMemo(
    () => ({
      label: formState.label,
      name: formState.name,
      fieldType: formState.fieldType,
      ...(formState.fieldType === 'select'
        ? { options: formState.options }
        : {}),
    }),
    [formState.label, formState.name, formState.fieldType, formState.options]
  );

  const hasUnsavedChanges = useMemo(
    () =>
      JSON.stringify(formState) !== JSON.stringify(initialFormStateRef.current),
    [formState]
  );

  // Handle form save (API calls)
  const handleSave = useCallback(async () => {
    if (!orgId || !userId) {
      return;
    }

    setIsSubmitting(true);
    setApiError(null);

    try {
      const eventsClient = createEventsClient(orgId);

      if (isEditMode && property) {
        // Edit existing property type
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
          startSeq: property.last_event_seq,
        });

        const properties: Record<string, unknown> = {
          ahoy_label: formState.label,
          ahoy_description: formState.description || null,
          // Note: field_type, name, is_unique, and is_required cannot be changed after creation
        };

        // Include options for select type
        if (formState.fieldType === 'select') {
          properties.ahoy_options = formState.options;
        }

        builder.propertiesUpdated(
          property.entity_id,
          'ahoy_property_type',
          properties
        );

        await eventsClient.submitEvents(builder.build());
      } else {
        // Create new property type
        const entityId = crypto.randomUUID();
        const builder = createEventBuilder({
          sourceType: 'ahoy_user',
          sourceId: String(userId),
        });

        const properties: Record<string, unknown> = {
          ahoy_type_name: formState.name,
          ahoy_entity_type_name: crmTypeName,
          ahoy_field_type_name: formState.fieldType,
          ahoy_label: formState.label,
          ahoy_description: formState.description || null,
          ahoy_is_unique_value: formState.isUnique ? 'true' : 'false',
        };

        // Include options for select type
        if (formState.fieldType === 'select') {
          properties.ahoy_options = formState.options;
        }

        builder.objectCreated(entityId, 'ahoy_property_type', properties);

        await eventsClient.submitEvents(builder.build());
      }

      // Invalidate query cache
      await queryClient.invalidateQueries({
        queryKey: ['propertyTypes', orgId, crmTypeName],
      });

      // Call success callback
      onSuccess?.();

      // Close slideout
      onClose();
    } catch (error) {
      setApiError(getUserFriendlyEventError(error));
    } finally {
      setIsSubmitting(false);
    }
  }, [
    orgId,
    userId,
    isEditMode,
    property,
    formState,
    crmTypeName,
    queryClient,
    onSuccess,
    onClose,
  ]);

  const handleClearApiError = useCallback(() => {
    setApiError(null);
  }, []);

  // Check if an option value is in use (for existing properties only)
  const handleCheckOptionUsage = useCallback(
    async (optionValue: string): Promise<number> => {
      if (!orgId || !property?.property_type_name) {
        return 0;
      }

      try {
        const client = createCRMObjectClient(orgId, crmTypeName);
        const response = await client.list({
          filter_groups: [
            {
              filters: [
                {
                  property: property.property_type_name,
                  operator: 'exact',
                  value: optionValue,
                },
              ],
            },
          ],
        });
        return response.count ?? 0;
      } catch {
        // On error, return 0 to allow deletion (fail open)
        return 0;
      }
    },
    [orgId, crmTypeName, property?.property_type_name]
  );

  const title = isEditMode ? 'Edit property' : 'Create property';

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  return (
    <SlideoutMenu
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      hasUnsavedChanges={hasUnsavedChanges}
      onDiscardUnsavedChanges={noop}
    >
      {({ close }) => (
        <FormProvider
          fields={fieldConfig}
          values={formValues}
          fieldLabels={FIELD_LABELS}
        >
          <PropertyTypeFormContent
            close={close}
            title={title}
            isEditMode={isEditMode}
            formState={formState}
            updateField={updateField}
            optionErrors={optionErrors}
            apiError={apiError}
            isSubmitting={isSubmitting}
            validateAndApplyOptionErrors={validateAndApplyOptionErrors}
            onSave={handleSave}
            onClearApiError={handleClearApiError}
            fieldTypes={fieldTypes}
            fieldTypesLoading={fieldTypesLoading}
            fieldTypesError={fieldTypesError}
            crmTypeName={crmTypeName}
            onCheckOptionUsage={handleCheckOptionUsage}
          />
        </FormProvider>
      )}
    </SlideoutMenu>
  );
}
